// ABOUTME: NPR News Now podcast widget with audio playback controls.
// ABOUTME: Fetches and plays the latest NPR hourly news update via RSS feed.
import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import NPRWidgetWarningIcon from "./NPRWidgetWarningIcon";
import { fetchFeedXml, FeedUnavailableError } from "../utils/feedProxy";

interface FeedItem {
  title: string;
  pubDate: string;
  audioUrl: string;
  description: string;
}

const PODCAST_URL = "https://www.npr.org/podcasts/500005/npr-news-now";

const NPRWidget: React.FC = () => {
  const [item, setItem] = useState<FeedItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Read by the refresh interval without being a dependency of it — otherwise
  // toggling play/pause tears the effect down and refetches the feed.
  const playingRef = useRef(playing);
  useLayoutEffect(() => {
    playingRef.current = playing;
  });

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    let cancelled = false;
    async function fetchFeed() {
      setLoading(true);
      try {
        const xml = await fetchFeedXml("npr-feed");
        const firstItem = xml.querySelector("item");
        if (!firstItem) throw new Error("Feed contained no episodes");
        const title = firstItem.querySelector("title")?.textContent || "";
        const pubDate = firstItem.querySelector("pubDate")?.textContent || "";
        const audioUrl = firstItem.querySelector("enclosure")?.getAttribute("url") || "";
        let description = firstItem.querySelector("description")?.textContent || "";
        // Only strip HTML tags, do not remove content or lines
        description = description.replace(/<[^>]+>/g, "");
        if (cancelled) return;
        setError("");
        setItem({ title, pubDate, audioUrl, description });
      } catch (err) {
        if (cancelled) return;
        // Say what actually went wrong. "Failed to load NPR feed" was the same
        // message whether the proxy was missing, the network was down, or NPR
        // returned an empty feed.
        if (err instanceof FeedUnavailableError) {
          console.error(`NPR widget: ${err.message}`, err.detail ?? "");
          setError(err.message);
        } else {
          console.error("NPR widget: unexpected failure", err);
          setError(err instanceof Error ? err.message : "Failed to load NPR feed");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchFeed();
    // Refresh every 45 min if not playing
    interval = setInterval(
      () => {
        if (!playingRef.current) fetchFeed();
      },
      45 * 60 * 1000
    );
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const timeUpdate = () => setCurrentTime(audio.currentTime);
    const loaded = () => setDuration(audio.duration);
    // Named so removeEventListener actually matches on cleanup — an inline
    // arrow here created a new identity and never detached.
    const ended = () => setPlaying(false);
    audio.addEventListener("timeupdate", timeUpdate);
    audio.addEventListener("loadedmetadata", loaded);
    audio.addEventListener("ended", ended);
    return () => {
      audio.removeEventListener("timeupdate", timeUpdate);
      audio.removeEventListener("loadedmetadata", loaded);
      audio.removeEventListener("ended", ended);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play();
      setPlaying(true);
    }
  };
  const seek = (sec: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(audio.currentTime + sec, duration));
    setCurrentTime(audio.currentTime);
  };
  const onScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const val = Number(e.target.value);
    audio.currentTime = val;
    setCurrentTime(val);
  };

  function fmt(sec: number) {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  }

  function fmtDate(dateStr: string) {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  return (
    <div className="pointer-events-auto px-3 py-3 rounded-[var(--radius-md)] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-xs text-[var(--color-text-tertiary)] hover:border-[var(--color-accent)] transition-colors flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <a
          href={PODCAST_URL}
          target="_blank"
          rel="noopener noreferrer"
          title="NPR News Now Podcast"
        >
          <img
            src="https://media.npr.org/chrome_svg/npr-logo.svg"
            alt="NPR logo"
            className="w-8 h-8 rounded bg-white p-0.5 hover:ring-2 hover:ring-[var(--color-accent)] transition"
          />
        </a>
        <span className="truncate font-heading font-semibold text-sm text-[var(--color-text-primary)] flex-1">
          {loading ? (
            <span className="text-xs text-[var(--color-text-muted)]">Loading...</span>
          ) : (
            <>
              {item?.title || "NPR Hourly News"}
              {item?.pubDate && (
                <span className="ml-2 text-xs text-[var(--color-text-tertiary)]">
                  ({fmtDate(item.pubDate)})
                </span>
              )}
            </>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2 [@media(pointer:coarse)]:gap-4 w-full min-w-0">
        <button
          className="tap-target flex items-center justify-center w-7 h-7 rounded-full bg-transparent hover:bg-[var(--color-bg-hover)] text-[var(--color-accent)] text-base focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] transition-colors"
          onClick={() => seek(-15)}
          title="Back 15s"
          aria-label="Back 15 seconds"
        >
          <svg width="14" height="14" viewBox="0 0 512 512" fill="currentColor">
            <path d="M459.5 440.6c9.5 7.9 22.8 9.7 34.1 4.4s18.4-16.6 18.4-29V96c0-12.4-7.2-23.7-18.4-29s-24.5-3.6-34.1 4.4L288 214.3V96c0-12.4-7.2-23.7-18.4-29s-24.5-3.6-34.1 4.4l-192 160c-7.3 6.1-11.5 15.1-11.5 24.6s4.2 18.5 11.5 24.6l192 160c9.5 7.9 22.8 9.7 34.1 4.4s18.4-16.6 18.4-29V297.7l171.5 142.9z" />
          </svg>
        </button>
        <button
          className="tap-target flex items-center justify-center w-8 h-8 rounded-full bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-text-inverse)] text-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] transition-colors"
          onClick={togglePlay}
          title={playing ? "Pause" : "Play"}
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? (
            <svg width="14" height="14" viewBox="0 0 320 512" fill="currentColor">
              <path d="M48 64C21.5 64 0 85.5 0 112v288c0 26.5 21.5 48 48 48h32c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48H48zm192 0c-26.5 0-48 21.5-48 48v288c0 26.5 21.5 48 48 48h32c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48h-32z" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 384 512" fill="currentColor">
              <path d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80v352c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.8 23-24.2 23-41s-8.7-32.2-23-41L73 39z" />
            </svg>
          )}
        </button>
        <button
          className="tap-target flex items-center justify-center w-7 h-7 rounded-full bg-transparent hover:bg-[var(--color-bg-hover)] text-[var(--color-accent)] text-base focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] transition-colors"
          onClick={() => seek(15)}
          title="Fwd 15s"
          aria-label="Forward 15 seconds"
        >
          <svg width="14" height="14" viewBox="0 0 512 512" fill="currentColor">
            <path d="M52.5 440.6c-9.5 7.9-22.8 9.7-34.1 4.4S0 428.4 0 416V96c0-12.4 7.2-23.7 18.4-29s24.5-3.6 34.1 4.4L224 214.3V96c0-12.4 7.2-23.7 18.4-29s24.5-3.6 34.1 4.4l192 160c7.3 6.1 11.5 15.1 11.5 24.6s-4.2 18.5-11.5 24.6l-192 160c-9.5 7.9-22.8 9.7-34.1 4.4S224 428.4 224 416V297.7L52.5 440.6z" />
          </svg>
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <input
            type="range"
            min={0}
            max={duration || 0}
            value={currentTime}
            onChange={onScrub}
            className="flex-1 accent-[var(--color-accent)] h-1 mx-2 min-w-0"
            step={0.1}
            aria-label="Seek"
            style={{ minWidth: 0 }}
          />
          <span className="text-xs text-[var(--color-text-tertiary)] w-14 text-right tabular-nums whitespace-nowrap">
            {fmt(currentTime)} / {fmt(duration)}
          </span>
        </div>
        {/* "metadata" fetches just enough for the duration readout. "auto"
            downloaded the entire multi-megabyte segment on every page load,
            whether or not the user ever pressed play. */}
        <audio ref={audioRef} src={item?.audioUrl} preload="metadata" />
      </div>
      {item && !loading && !error && item.description && (
        <div
          className="text-xs text-[var(--color-text-tertiary)] line-clamp-1 mt-0.5"
          title={item.description}
        >
          {item.description}
        </div>
      )}
      {error && (
        <span className="flex items-center text-xs text-[var(--color-warning)] gap-1.5 mt-1">
          <NPRWidgetWarningIcon message={error} />
          {/* Previously sr-only, which meant a broken widget looked identical
              to a working one for sighted users. */}
          <span className="min-w-0 truncate" title={error}>
            {error}
          </span>
        </span>
      )}
    </div>
  );
};

export default NPRWidget;
